import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment
} from 'firebase/firestore';

export default function App() {
  const [nieInput, setNieInput] = useState('');
  const [sectionSelected, setSectionSelected] = useState('');
  const [sections, setSections] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Bajo consumo
  const [lowUsageMode, setLowUsageMode] = useState(false);
  const [loadingLowUsage, setLoadingLowUsage] = useState(true);

  const validNie = /^[0-9]+$/;

  const statusMap = {
    P: 'Presente',
    T: 'Tarde',
    A: 'Ausente',
    J: 'Justificado'
  };

  const statusStyle = (status) => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    color: '#fff',
    fontWeight: 500,
    marginLeft: '0.5rem',
    backgroundColor:
      status === 'P' ? '#28a745' :
      status === 'T' ? '#fd7e14' :
      status === 'A' ? '#dc3545' :
      status === 'J' ? '#17a2b8' :
      '#7f8c8d'
  });

  // Carga de secciones
  useEffect(() => {
    const loadSections = async () => {
      try {
        const q = query(collection(db, 'clases_institucionales'));
        const snap = await getDocs(q);
        const list = snap.docs.map(doc => doc.data().section);
        const unique = [...new Set(list)];
        unique.sort((a, b) => {
          const [g1, s1] = a.split('-').map(Number);
          const [g2, s2] = b.split('-').map(Number);
          return g1 !== g2 ? g1 - g2 : s1 - s2;
        });
        setSections(unique);
      } catch (err) {
        console.error('Error cargando secciones', err);
      }
    };
    loadSections();
  }, []);

  // Validar NIE
  useEffect(() => {
    if (nieInput.trim() && !validNie.test(nieInput.trim())) {
      setErrorMessage('El NIE debe ser un número sin espacios ni letras.');
    } else {
      setErrorMessage('');
    }
  }, [nieInput]);

  // Bajo consumo: consulta al inicio
  useEffect(() => {
    async function checkLowUsage() {
      const today = new Date().toISOString().slice(0,10);
      const ref = doc(db, 'app_stats', today);
      let lecturasHoy = 0;
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) {
          lecturasHoy = snap.data().lecturasHoy || 0;
        } else {
          await setDoc(ref, { lecturasHoy: 0 });
        }
      } catch (err) {
        // Si falla, nunca bloquees la app: sigue en modo normal
      }
      setLowUsageMode(lecturasHoy >= 40000);
      setLoadingLowUsage(false);
    }
    checkLowUsage();
  }, []);

  const isButtonDisabled =
    !(nieInput.trim() && validNie.test(nieInput.trim()) && sectionSelected) ||
    loading || loadingLowUsage || lowUsageMode;

  const handleConsult = async () => {
    setLoading(true);
    setErrorMessage('');
    setReportData(null);

    // Incrementa contador de lecturas antes de consultar
    try {
      const today = new Date().toISOString().slice(0,10);
      const ref = doc(db, 'app_stats', today);
      await updateDoc(ref, { lecturasHoy: increment(1) });
    } catch (err) {
      // Si falla, la consulta igual sigue (no bloquees al usuario)
    }

    try {
      // Buscar la clase por sección
      const classQuery = query(
        collection(db, 'clases_institucionales'),
        where('section', '==', sectionSelected)
      );
      const classSnap = await getDocs(classQuery);

      if (classSnap.empty) {
        setErrorMessage('Sección no encontrada.');
        setLoading(false);
        return;
      }

      const classDoc = classSnap.docs[0];
      const classId = classDoc.id;

      // Buscar el alumno por NIE
      const studentQuery = query(
        collection(db, 'clases_institucionales', classId, 'students'),
        where('nie', '==', nieInput.trim())
      );
      const studentSnap = await getDocs(studentQuery);

      if (studentSnap.empty) {
        setErrorMessage('No se encontró el NIE en esta sección.');
        setLoading(false);
        return;
      }

      const studentDoc = studentSnap.docs[0];
      const studentId = studentDoc.id;
      const studentData = studentDoc.data();

      // Buscar las últimas 6 asistencias y leer el recordsMap directamente
      const attendanceQuery = query(
        collection(db, 'clases_institucionales', classId, 'attendance'),
        orderBy('date', 'desc'),
        limit(6)
      );
      const attendanceSnap = await getDocs(attendanceQuery);

      const attendanceRecords = [];
      for (const attDoc of attendanceSnap.docs) {
        const data = attDoc.data();
        const recordsMap = data.recordsMap || {};
        const status = recordsMap[studentId];
        if (status) {
          attendanceRecords.push({
            date: data.date.toDate().toLocaleDateString(),
            status
          });
        }
      }

      setReportData({
        fullName: `${studentData.apellidos}, ${studentData.nombres}`,
        section: sectionSelected,
        attendance: attendanceRecords
      });
    } catch (err) {
      console.error('Error consultando Firestore', err);
      setErrorMessage('Ocurrió un error al consultar los datos.');
    } finally {
      setLoading(false);
    }
  };

  // Render
  return (
    <div style={{ padding: '2rem', maxWidth: '400px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <img src="/banner_v2.png" alt="Logo" style={{ width: '141px', height: '39px' }} />
      </div>

      {/* Título */}
      <h1 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Consulta de asistencia para padres</h1>

      {/* Si modo bajo consumo está activo */}
      {loadingLowUsage ? (
        <p style={{ textAlign: 'center' }}>Cargando...</p>
      ) : lowUsageMode ? (
        <div style={{ color: '#c0392b', background: '#fffbe7', border: '1px solid #ffe08a', borderRadius: 8, padding: '1rem', margin: '1rem 0', textAlign: 'center' }}>
          <h3 style={{ marginTop: 0 }}>Modo ahorro activado</h3>
          <p>Por alta demanda hoy, la consulta estará disponible nuevamente mañana.<br/>
          Si tienes dudas urgentes, contacta directamente al Instituto.</p>
        </div>
      ) : (
        <>
          {/* Campo NIE */}
          <div style={{ marginBottom: '1rem' }}>
            <label>NIE:</label>
            <input
              type="text"
              value={nieInput}
              onChange={(e) => setNieInput(e.target.value)}
              placeholder="Ingrese el NIE"
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', fontSize: '1rem' }}
            />
          </div>

          {/* Dropdown sección */}
          <div style={{ marginBottom: '1rem' }}>
            <label>Sección:</label>
            <select
              value={sectionSelected}
              onChange={(e) => setSectionSelected(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', fontSize: '1rem' }}
            >
              <option value="">Seleccione una sección</option>
              {sections.map((section) => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
            </select>
          </div>

          {/* Mensaje de error */}
          {errorMessage && <p style={{ color: '#dc3545', fontWeight: 'bold', textAlign: 'center' }}>{errorMessage}</p>}

          {/* Botón */}
          <button onClick={handleConsult} disabled={isButtonDisabled} style={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}>
            {loading ? 'Consultando...' : 'Consultar'}
          </button>
        </>
      )}

      {/* Reporte */}
      {reportData && (
        <div className="report-container" style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '1.5rem', marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Reporte de Asistencia</h3>
          <p><strong>NIE:</strong> {nieInput}</p>
          <p><strong>Nombre Completo:</strong> {reportData.fullName}</p>
          <p><strong>Sección:</strong> {reportData.section}</p>
          <h4>Últimas 6 Asistencias:</h4>
          {reportData.attendance.length > 0 ? (
            <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
              {reportData.attendance.map((record, index) => (
                <li key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                  {record.date}
                  <span style={statusStyle(record.status)}>
                    {statusMap[record.status] || record.status}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p>No hay registros recientes.</p>
          )}
          <p style={{ textAlign: 'center', marginBottom: '0rem' }}><strong>Consulta de asistencia para padres</strong></p>
          <p style={{ textAlign: 'center', marginTop: '0.5rem' }}>Solo se muestran las 6 asistencias más recientes. Si necesitas más información, consulta con tu docente o acércate al instituto.</p>
        </div>
      )}
    </div>
  );
}
